import { FileText } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { hiddenInPrint } from '@/styles/print.css';

type CreatePdfButtonProps = {
    /**
     * Runs as printing starts, for state the page has to carry onto paper —
     * expanding a paginated table, say. The browser captures the page as soon as
     * printing begins, so a caller whose update has to be on the page by then is
     * responsible for committing it (`flushSync`).
     */
    beforeOpenPrint?: () => void;
    /** Runs once printing is over, to undo whatever `beforeOpenPrint` set up. */
    afterClosePrint?: () => void;
};

/**
 * Opens the browser's print dialog for the current screen, where the user picks
 * "Save as PDF" as the destination. The destination can't be preselected from
 * script, so the dialog is as far as the page can take them.
 */
export function CreatePdfButton({ beforeOpenPrint, afterClosePrint }: CreatePdfButtonProps) {
    /*
     * Hooked to the print events rather than to the click, so the callbacks also
     * run when printing is started from the browser itself (Ctrl+P) — and so
     * `afterClosePrint` has an event to hang off at all.
     */
    useEffect(() => {
        const handleBeforePrint = () => beforeOpenPrint?.();
        const handleAfterPrint = () => afterClosePrint?.();

        window.addEventListener('beforeprint', handleBeforePrint);
        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, [beforeOpenPrint, afterClosePrint]);

    return (
        <Button variant="secondary" className={hiddenInPrint} onClick={() => window.print()}>
            <Icon icon={FileText} size="sm" />
            PDFを作成する
        </Button>
    );
}
